<template>
	<div id="clientAnalytics" class="jc-view">
		<JuniperPageHeader
			:back="'/juniper/clients/edit/' + clientId"
			:title="client ? client.name : ''"
			subtitle="Analytics"
		/>

		<ul class="nav nav-tabs mt-3" role="tablist">
			<li class="nav-item" role="presentation">
				<button
					class="nav-link"
					:class="{ active: activeTab === 'general' }"
					type="button"
					@click="activeTab = 'general'"
				>
					<i class="fas fa-chart-line me-1"></i> General
				</button>
			</li>
			<li class="nav-item" role="presentation">
				<button
					class="nav-link"
					:class="{ active: activeTab === 'settings' }"
					type="button"
					@click="activeTab = 'settings'"
				>
					<i class="fas fa-cog me-1"></i> Settings
				</button>
			</li>
		</ul>

		<div class="ca-tab-content">
			<!-- ====================== GENERAL ====================== -->
			<div v-show="activeTab === 'general'">
				<div class="ca-layout">
					<aside class="ca-intro">
						<h2 class="ca-intro__title">Page Views</h2>
						<p class="ca-intro__desc">
							Google Analytics views for every page and news &amp; notice on this site. Counts are stored in Juniper and refreshed in the background every {{ intervalLabel(state.sync_interval) }}, so the dashboards never wait on Google.
						</p>

						<div class="ca-sync-box">
							<div class="ca-sync-box__row">
								<span class="ca-sync-box__label">Last synced</span>
								<span class="ca-sync-box__value">
									<template v-if="lastSync && lastSync.synced_at">
										<span :title="formatUtc(lastSync.synced_at)">{{ fromNowUtc(lastSync.synced_at) }}</span>
									</template>
									<span v-else class="text-muted">Never</span>
								</span>
							</div>
							<div class="ca-sync-box__row">
								<span class="ca-sync-box__label">Next sync</span>
								<span class="ca-sync-box__value">
									<span v-if="state.next_sync_at" :title="formatUtc(state.next_sync_at)">{{ nextSyncLabel }}</span>
									<span v-else class="text-muted">On next visit</span>
								</span>
							</div>
							<button class="ca-btn ca-btn--sm mt-2" :disabled="syncing" @click="syncNow">
								<i class="fas fa-sync" :class="{ 'fa-spin': syncing }"></i>
								{{ syncing ? 'Syncing…' : 'Sync now' }}
							</button>
						</div>
					</aside>

					<section class="ca-content">
						<div v-if="lastSync && lastSync.status === 'failed'" class="ca-alert">
							<i class="fas fa-exclamation-triangle me-2"></i>
							<div>
								<strong>Last sync failed</strong> {{ fromNowUtc(lastSync.attempted_at) }}.
								<span class="ca-alert__error">{{ lastSync.error }}</span>
							</div>
						</div>

						<div class="ca-stats">
							<div class="ca-stat">
								<span class="ca-stat__label">Page views · 30 days</span>
								<span class="ca-stat__value">{{ formatViews(totals.pages.last_30_days_views) }}</span>
								<span class="ca-stat__sub">{{ formatViews(totals.pages.all_time_views) }} all time</span>
							</div>
							<div class="ca-stat">
								<span class="ca-stat__label">News &amp; Notices views · 30 days</span>
								<span class="ca-stat__value">{{ formatViews(totals.news_notices.last_30_days_views) }}</span>
								<span class="ca-stat__sub">{{ formatViews(totals.news_notices.all_time_views) }} all time</span>
							</div>
						</div>

						<div class="ca-card">
							<div class="ca-card__header">
								<div class="ca-segmented" role="group">
									<button
										type="button"
										:class="{ active: type === 'pages' }"
										@click="setType('pages')"
									>
										Pages <span class="ca-segmented__count">{{ totals.pages.items }}</span>
									</button>
									<button
										type="button"
										:class="{ active: type === 'news_notices' }"
										@click="setType('news_notices')"
									>
										News &amp; Notices <span class="ca-segmented__count">{{ totals.news_notices.items }}</span>
									</button>
								</div>
								<div class="ca-card__header-actions">
									<input
										v-model="search"
										type="search"
										class="form-control form-control-sm ca-search"
										placeholder="Search title or slug"
										@input="onSearch"
									/>
									<button
										class="ca-btn ca-btn--sm"
										:disabled="loadingItems"
										@click="loadItems(1)"
									>
										<i class="fas fa-sync" :class="{ 'fa-spin': loadingItems }"></i>
										Reload
									</button>
								</div>
							</div>

							<div v-if="loadingItems && items.length === 0" class="ca-empty">Loading…</div>
							<div v-else-if="items.length === 0" class="ca-empty">
								{{ search ? 'Nothing matches your search.' : (type === 'pages' ? 'No pages yet for this client.' : 'No news & notices yet for this client.') }}
							</div>
							<div v-else class="table-responsive">
								<table class="table align-middle mb-0 ca-table">
									<thead>
										<tr>
											<th>
												<button class="ca-sort" @click="sortBy('title')">
													Title <i class="fas" :class="sortIcon('title')"></i>
												</button>
											</th>
											<th>Status</th>
											<th class="text-end">
												<button class="ca-sort" @click="sortBy('last_30_days_views')">
													Last 30 days <i class="fas" :class="sortIcon('last_30_days_views')"></i>
												</button>
											</th>
											<th class="text-end">
												<button class="ca-sort" @click="sortBy('all_time_views')">
													All time <i class="fas" :class="sortIcon('all_time_views')"></i>
												</button>
											</th>
										</tr>
									</thead>
									<tbody>
										<tr v-for="item in items" :key="item.id">
											<td>
												<div class="ca-title">{{ item.title || item.slug }}</div>
												<a
													v-if="item.url"
													:href="item.url"
													target="_blank"
													rel="noopener"
													class="ca-path"
												>{{ item.path }} <i class="fas fa-external-link-alt"></i></a>
												<span v-else class="ca-path">{{ item.path }}</span>
											</td>
											<td>
												<span class="ca-status" :class="'ca-status--' + item.status">{{ statusLabel(item.status) }}</span>
											</td>
											<td class="text-end ca-num">{{ formatViews(item.last_30_days_views) }}</td>
											<td class="text-end ca-num">{{ formatViews(item.all_time_views) }}</td>
										</tr>
									</tbody>
								</table>
							</div>

							<div v-if="hasMoreItems || (loadingItems && items.length > 0)" class="ca-card__footer ca-card__footer--centered">
								<button class="ca-link-btn" :disabled="loadingItems || !hasMoreItems" @click="loadItems(page + 1, true)">
									<i v-if="loadingItems" class="fas fa-spinner fa-spin me-1"></i>
									{{ loadingItems ? 'Loading…' : 'Load more' }}
								</button>
								<span class="text-muted small">{{ items.length }} of {{ pageMeta.total || items.length }}</span>
							</div>
						</div>
					</section>
				</div>
			</div>

			<!-- ====================== SETTINGS ====================== -->
			<div v-show="activeTab === 'settings'">
				<div class="ca-layout">
					<aside class="ca-intro">
						<h2 class="ca-intro__title">Sync Interval</h2>
						<p class="ca-intro__desc">
							How often the view counts are refreshed from Google Analytics. A refresh is queued when someone opens a pages or news list after the interval has passed. A failed sync is retried after an hour.
						</p>
					</aside>
					<section class="ca-content">
						<div class="ca-card ca-card--pad">
							<div v-if="loadingState" class="text-muted">Loading…</div>
							<template v-else>
								<div class="ca-field">
									<label>Refresh every</label>
									<select v-model.number="form.sync_interval" class="form-select">
										<option v-for="hours in state.sync_intervals" :key="hours" :value="hours">
											{{ intervalLabel(hours) }}{{ hours === state.default_sync_interval ? ' (default)' : '' }}
										</option>
									</select>
								</div>
							</template>
						</div>
					</section>
				</div>

				<hr class="ca-divider" />

				<div class="ca-layout">
					<aside class="ca-intro">
						<h2 class="ca-intro__title">Last Sync</h2>
						<p class="ca-intro__desc">
							Outcome of the most recent refresh. Google Analytics data comes from the property set as the client's GA code in mc2.
						</p>
					</aside>
					<section class="ca-content">
						<div class="ca-card ca-card--pad">
							<div v-if="!lastSync" class="text-muted">Not synced yet.</div>
							<dl v-else class="ca-dl">
								<dt>Status</dt>
								<dd><span class="ca-status" :class="'ca-status--' + lastSync.status">{{ lastSync.status }}</span></dd>
								<dt>Attempted</dt>
								<dd>{{ formatUtc(lastSync.attempted_at) }}</dd>
								<dt>Last successful</dt>
								<dd>{{ lastSync.synced_at ? formatUtc(lastSync.synced_at) : '—' }}</dd>
								<dt>Pages</dt>
								<dd>{{ lastSync.pages == null ? '—' : lastSync.pages }}</dd>
								<dt>News &amp; Notices</dt>
								<dd>{{ lastSync.news_notices == null ? '—' : lastSync.news_notices }}</dd>
								<dt>Duration</dt>
								<dd>{{ lastSync.duration_ms == null ? '—' : (lastSync.duration_ms / 1000).toFixed(1) + 's' }}</dd>
								<template v-if="lastSync.error">
									<dt>Error</dt>
									<dd class="ca-dl__error">{{ lastSync.error }}</dd>
								</template>
							</dl>
						</div>
					</section>
				</div>

				<hr class="ca-divider" />

				<div class="ca-save-row">
					<button class="ca-save-btn" :disabled="savingSettings || loadingState" @click="saveSettings">
						<i class="fas fa-save"></i>
						{{ savingSettings ? 'Saving…' : 'Save changes' }}
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import juniperApi from '@/api/juniperApi'
import { formatUtc, fromNowUtc, utcToLocal } from '@/utils/time'

const emptyTotals = () => ({
	pages: { items: 0, all_time_views: null, last_30_days_views: null },
	news_notices: { items: 0, all_time_views: null, last_30_days_views: null },
})

export default {
	name: 'ClientAnalytics',
	data() {
		return {
			activeTab: 'general',
			client: null,

			// Sync state + settings (GET /clients/{id}/analytics/settings)
			state: {
				sync_interval: 24,
				sync_intervals: [1, 3, 6, 12, 24],
				default_sync_interval: 24,
				last_sync: null,
				next_sync_at: null,
				totals: emptyTotals(),
			},
			loadingState: false,
			form: { sync_interval: 24 },
			savingSettings: false,
			syncing: false,

			// Content list
			type: 'pages',
			search: '',
			searchTimer: null,
			orderby: 'last_30_days_views',
			order: 'desc',
			items: [],
			page: 1,
			perPage: 25,
			pageMeta: { current_page: 1, last_page: 1, total: 0 },
			loadingItems: false,
			// Drops responses of superseded requests (type/search/sort changed mid-flight).
			itemsRequestId: 0,
		}
	},
	computed: {
		clientId() {
			return this.$route.params.id
		},
		lastSync() {
			return this.state.last_sync
		},
		totals() {
			return this.state.totals || emptyTotals()
		},
		hasMoreItems() {
			return this.pageMeta.current_page < this.pageMeta.last_page
		},
		nextSyncLabel() {
			if (!this.state.next_sync_at) return ''
			return utcToLocal(this.state.next_sync_at).isBefore() ? 'Due — on next visit' : fromNowUtc(this.state.next_sync_at)
		},
	},
	async created() {
		await Promise.all([this.loadClient(), this.loadState(), this.loadItems(1)])
	},
	beforeDestroy() {
		clearTimeout(this.searchTimer)
	},
	methods: {
		formatUtc,
		fromNowUtc,

		intervalLabel(hours) {
			return hours === 1 ? '1 hour' : hours + ' hours'
		},
		// null = never synced / no GA data for this row ("—"); 0 = synced, no views.
		formatViews(value) {
			if (value === null || value === undefined) return '—'
			return Number(value).toLocaleString()
		},
		statusLabel(status) {
			return status === 'publish' ? 'published' : status
		},

		async loadClient() {
			try {
				const { data } = await juniperApi.get('/clients/' + this.clientId)
				this.client = data
			} catch (e) {
				console.error('Failed to load client', e)
			}
		},

		applyState(data) {
			this.state = Object.assign({}, this.state, data)
			this.form.sync_interval = data.sync_interval
		},

		async loadState() {
			this.loadingState = true
			try {
				const { data } = await juniperApi.get('/clients/' + this.clientId + '/analytics/settings')
				this.applyState(data)
			} catch (e) {
				console.error('Failed to load analytics settings', e)
			} finally {
				this.loadingState = false
			}
		},

		async loadItems(page = 1, append = false) {
			const requestId = ++this.itemsRequestId
			this.loadingItems = true
			try {
				const params = {
					type: this.type,
					orderby: this.orderby,
					order: this.order,
					per_page: this.perPage,
					page,
				}
				if (this.search.trim()) params.search = this.search.trim()
				const { data } = await juniperApi.get('/clients/' + this.clientId + '/analytics/content', { params })
				if (requestId !== this.itemsRequestId) return
				this.items = append ? this.items.concat(data.data || []) : data.data || []
				this.page = data.current_page || page
				this.pageMeta = {
					current_page: data.current_page || page,
					last_page: data.last_page || 1,
					total: data.total || (data.data || []).length,
				}
			} catch (e) {
				if (requestId !== this.itemsRequestId) return
				console.error('Failed to load analytics content', e)
				if (!append) {
					this.items = []
					this.pageMeta = { current_page: 1, last_page: 1, total: 0 }
				}
			} finally {
				if (requestId === this.itemsRequestId) this.loadingItems = false
			}
		},

		setType(type) {
			if (this.type === type) return
			this.type = type
			this.loadItems(1)
		},
		onSearch() {
			clearTimeout(this.searchTimer)
			this.searchTimer = setTimeout(() => this.loadItems(1), 300)
		},
		sortBy(field) {
			if (this.orderby === field) {
				this.order = this.order === 'desc' ? 'asc' : 'desc'
			} else {
				this.orderby = field
				this.order = field === 'title' ? 'asc' : 'desc'
			}
			this.loadItems(1)
		},
		sortIcon(field) {
			if (this.orderby !== field) return 'fa-sort ca-sort__idle'
			return this.order === 'asc' ? 'fa-sort-up' : 'fa-sort-down'
		},

		async syncNow() {
			this.syncing = true
			try {
				const { data } = await juniperApi.post('/clients/' + this.clientId + '/analytics/sync')
				this.applyState(data)
				const failed = data.last_sync && data.last_sync.status === 'failed'
				if (this.$toast) {
					failed ? this.$toast.error('Sync failed: ' + data.last_sync.error) : this.$toast.success('View counts refreshed')
				}
				await this.loadItems(1)
			} catch (e) {
				console.error(e)
				const msg = (e && e.response && e.response.data && e.response.data.message) || 'Failed to sync analytics'
				if (this.$toast) this.$toast.error(msg)
			} finally {
				this.syncing = false
			}
		},

		async saveSettings() {
			this.savingSettings = true
			try {
				const { data } = await juniperApi.patch('/clients/' + this.clientId + '/analytics/settings', {
					sync_interval: this.form.sync_interval,
				})
				this.applyState(data)
				if (this.$toast) this.$toast.success('Analytics settings saved')
			} catch (e) {
				console.error(e)
				const msg = (e && e.response && e.response.data && e.response.data.message) || 'Failed to save settings'
				if (this.$toast) this.$toast.error(msg)
			} finally {
				this.savingSettings = false
			}
		},
	},
}
</script>

<style scoped lang="scss">
/* Same look as ClientDeploy (cd-*): intro column + flat card, compact table. */
.ca-tab-content {
	padding: 15px;
}

.ca-layout {
	display: grid;
	grid-template-columns: 30% 1fr;
	gap: 1.5rem;
	align-items: start;
}

@media (max-width: 991px) {
	.ca-layout {
		grid-template-columns: 1fr;
	}
}

.ca-intro {
	padding: 0.25rem 1rem 0 0;
}

.ca-intro__title {
	font-size: 1.4rem;
	font-weight: 700;
	color: #2b303b;
	margin: 0 0 0.5rem;
	letter-spacing: -0.01em;
}

.ca-intro__desc {
	font-size: 0.85rem;
	color: #8a909c;
	line-height: 1.5;
	margin: 0;
}

.ca-sync-box {
	margin-top: 1.25rem;
	padding-top: 1rem;
	border-top: 1px solid #f0f1f4;
}

.ca-sync-box__row {
	display: flex;
	justify-content: space-between;
	gap: 0.5rem;
	font-size: 0.82rem;
	padding: 0.15rem 0;
}

.ca-sync-box__label {
	color: #8a909c;
}

.ca-sync-box__value {
	color: #2b303b;
	font-weight: 600;
}

.ca-content {
	display: flex;
	flex-direction: column;
	gap: 1rem;
	min-width: 0;
}

.ca-alert {
	display: flex;
	align-items: flex-start;
	padding: 0.7rem 1rem;
	border: 1px solid #fecaca;
	border-left-width: 4px;
	border-radius: 10px;
	background: #fff5f5;
	color: #991b1b;
	font-size: 0.85rem;
}

.ca-alert i {
	margin-top: 0.15rem;
	color: #dc2626;
}

.ca-alert__error {
	display: block;
	color: #b45454;
	margin-top: 0.15rem;
}

.ca-stats {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 1rem;
}

@media (max-width: 575px) {
	.ca-stats {
		grid-template-columns: 1fr;
	}
}

.ca-stat {
	display: flex;
	flex-direction: column;
	padding: 0.9rem 1.1rem;
	border: 1px solid #e6e8ec;
	border-radius: 12px;
}

.ca-stat__label {
	font-size: 0.78rem;
	font-weight: 600;
	color: #8a909c;
}

.ca-stat__value {
	font-size: 1.6rem;
	font-weight: 700;
	color: #2b303b;
	font-variant-numeric: tabular-nums;
	line-height: 1.3;
}

.ca-stat__sub {
	font-size: 0.78rem;
	color: #8a909c;
}

.ca-card {
	border: 1px solid #e6e8ec;
	border-radius: 12px;
	background: transparent;
	overflow: hidden;
}

.ca-card--pad {
	padding: 1.15rem;
}

.ca-card__header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	flex-wrap: wrap;
	padding: 0.7rem 1rem;
	border-bottom: 1px solid #f0f1f4;
	gap: 0.5rem;
}

.ca-card__header-actions {
	display: flex;
	align-items: center;
	gap: 0.5rem;
}

.ca-search {
	width: 220px;
	border-radius: 7px;
	border-color: #e6e8ec;
	font-size: 0.8rem;
}

.ca-card__footer {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 0.65rem 1rem;
	border-top: 1px solid #f0f1f4;
	background: #f8f9fb;
}

.ca-card__footer--centered {
	justify-content: center;
	gap: 0.75rem;
}

.ca-segmented {
	display: inline-flex;
	padding: 2px;
	border-radius: 8px;
	background: #f3f5f9;
}

.ca-segmented button {
	border: 0;
	background: transparent;
	padding: 0.25rem 0.7rem;
	border-radius: 6px;
	font-size: 0.8rem;
	font-weight: 600;
	color: #6c757d;
	cursor: pointer;
}

.ca-segmented button.active {
	background: #fff;
	color: #2b303b;
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}

.ca-segmented__count {
	margin-left: 0.25rem;
	color: #adb5bd;
	font-weight: 500;
}

.ca-empty {
	padding: 2rem 1rem;
	text-align: center;
	color: #8a909c;
}

.ca-table {
	font-size: 13px;
	margin-bottom: 0;
}

.ca-table thead th {
	font-size: 13px;
	font-weight: 600;
	color: #6c757d;
	padding: 4px 10px 4px 0.5rem;
	border-bottom: 1px solid #dee2e6;
	background: transparent;
	white-space: nowrap;
}

.ca-table tbody td {
	padding: 6px 10px 6px 0.5rem;
	border-color: #f1f3f5;
	vertical-align: middle;
}

.ca-table tbody tr:last-child td {
	border-bottom: none;
}

.ca-sort {
	border: 0;
	background: none;
	padding: 0;
	font: inherit;
	color: inherit;
	cursor: pointer;
	white-space: nowrap;
}

.ca-sort i {
	margin-left: 0.2rem;
	font-size: 0.72rem;
}

.ca-sort__idle {
	opacity: 0.35;
}

.ca-title {
	color: #2b303b;
	font-weight: 600;
}

.ca-path {
	font-size: 12px;
	color: #8a909c;
	text-decoration: none;
	word-break: break-all;
}

a.ca-path:hover {
	color: #2b6cff;
}

.ca-path i {
	font-size: 0.65rem;
	margin-left: 0.15rem;
}

.ca-num {
	font-variant-numeric: tabular-nums;
	white-space: nowrap;
}

.ca-status {
	font-size: 0.7rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.04em;
	padding: 0.12rem 0.45rem;
	border-radius: 5px;
	display: inline-block;
	background: #f1f3f5;
	color: #6c757d;
}

.ca-status--publish,
.ca-status--success {
	background: #e6f8ef;
	color: #117a45;
}

.ca-status--draft,
.ca-status--future {
	background: #fff3e0;
	color: #c97300;
}

.ca-status--failed {
	background: #ffe9e9;
	color: #c93434;
}

.ca-field label {
	display: block;
	font-size: 0.78rem;
	font-weight: 600;
	color: #4a5260;
	margin-bottom: 0.35rem;
}

.ca-field ::v-deep .form-select {
	border-radius: 9px;
	border-color: #e6e8ec;
	font-size: 0.9rem;
	padding: 0.5rem 0.75rem;
}

.ca-dl {
	display: grid;
	grid-template-columns: max-content 1fr;
	gap: 0.4rem 1.25rem;
	margin: 0;
	font-size: 0.85rem;
}

.ca-dl dt {
	font-weight: 600;
	color: #8a909c;
}

.ca-dl dd {
	margin: 0;
	color: #2b303b;
}

.ca-dl__error {
	color: #c93434 !important;
	word-break: break-word;
}

.ca-btn {
	display: inline-flex;
	align-items: center;
	gap: 0.4rem;
	padding: 0.45rem 0.9rem;
	border: 1px solid #e6e8ec;
	border-radius: 9px;
	font-size: 0.85rem;
	font-weight: 600;
	background: transparent;
	color: #2b303b;
	cursor: pointer;
	transition: all 0.12s ease;
}

.ca-btn:hover:not(:disabled) {
	background: #f3f5f9;
}

.ca-btn:disabled {
	opacity: 0.55;
	cursor: not-allowed;
}

.ca-btn--sm {
	padding: 0.25rem 0.6rem;
	font-size: 0.78rem;
	border-radius: 7px;
	gap: 0.3rem;
}

.ca-link-btn {
	display: inline-flex;
	align-items: center;
	gap: 0.3rem;
	background: none;
	border: 0;
	padding: 0;
	color: #adb5bd;
	font-size: 0.85rem;
	font-weight: 600;
	cursor: pointer;
	transition: color 0.12s ease;
}

.ca-link-btn:hover:not(:disabled) {
	color: #2b303b;
}

.ca-link-btn:disabled {
	opacity: 0.55;
	cursor: default;
}

.ca-divider {
	border: none;
	border-top: 1px solid #c8ccd4;
	margin: 1.5rem 15px;
}

.ca-save-row {
	display: flex;
	justify-content: flex-end;
}

.ca-save-btn {
	display: inline-flex;
	align-items: center;
	gap: 0.35rem;
	padding: 0.35rem 0.9rem;
	background: transparent;
	border: 1px solid #e6e8ec;
	border-radius: 10px;
	color: #2b303b;
	font-size: 0.9rem;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.15s ease;
}

.ca-save-btn:hover:not(:disabled) {
	background: #f3f5f9;
}

.ca-save-btn:disabled {
	opacity: 0.55;
	cursor: not-allowed;
}
</style>
